import { Spacing } from '@persona-id/design-tokens';
import { CircleInfoIcon } from '@persona-id/design-tokens/icons';
import { mapValues, merge, startCase } from 'lodash/fp';
import { ChangeEvent, useMemo, VFC } from 'react';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import ReactSelect from 'dashboard/components/ReactSelect';
import Tooltip from 'dashboard/components/Tooltip';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { StepAdvancedConfigurationWithErrorHandlingPanel } from 'dashboard/components/WorkflowControlPanel/StepAdvancedConfigurationWithErrorHandlingPanel';
import EditObjectKeyValuePairsPanel from 'dashboard/components/WorkflowControlPanel/StepConfigAction/EditObjectKeyValuePairsPanel';
import FieldsList from 'dashboard/components/WorkflowControlPanel/StepConfigAction/FieldsList';
import { ParamsBaseProps } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsBaseProps';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { callHelper, formatHandlebarValue, getHandlebars } from 'dashboard/lib/utils/handlebars';
import { generateJsonSchemaConfig, valueTransformer } from 'dashboard/lib/workflows';
import {
  filterUsableObjects,
  flattenedEntriesToHash,
  flattenKeyValue,
  KeyValue,
  Operator,
  StringKeyValue,
  UsableObjectConfig,
} from 'dashboard/lib/workflows/objects';
import { Trigger } from 'dashboard/models/trigger';
import {
  WorkflowError,
  WorkflowFieldObjectKey,
  WorkflowObjectKey,
} from 'dashboard/models/workflow';
import { WorkflowActionUpdateObjectV1 } from 'dashboard/models/workflow-version';
import { useSchemaForFieldPath } from 'dashboard/redux/selectors/workflow-editor-context';
import Checkbox from 'design-system/components/Checkbox';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import { JsonSchemaAllTypes, JsonSchemaCustomTypes } from 'lib/fields';
import useToggle from 'lib/hooks/useToggle';
import { JsonSchema, JsonSchemaObject, JsonSchemaTypes } from 'lib/json-schema';

// This mirrors DEPRECATED_PROPERTY_KEYS in the BE models/account.rb as well as logic in
// update_object_v1 to handle deprecated properties for accounts. Once these properties
// are migrated to fields, they can be removed.
type KeyConfig = {
  key: string;
  type: JsonSchemaTypes | JsonSchemaCustomTypes;
};
const ACCOUNT_PRIMARY_PROPERTY_CONFIGS: KeyConfig[] = [
  {
    key: 'name_first',
    type: JsonSchemaAllTypes.String,
  },
  {
    key: 'name_middle',
    type: JsonSchemaAllTypes.String,
  },
  {
    key: 'name_last',
    type: JsonSchemaAllTypes.String,
  },
  {
    key: 'country_code',
    type: JsonSchemaAllTypes.String,
  },
  {
    key: 'address_street_1',
    type: JsonSchemaAllTypes.String,
  },
  {
    key: 'address_street_2',
    type: JsonSchemaAllTypes.String,
  },
  {
    key: 'address_subdivision',
    type: JsonSchemaAllTypes.String,
  },
  {
    key: 'address_city',
    type: JsonSchemaAllTypes.String,
  },
  {
    key: 'address_postal_code',
    type: JsonSchemaAllTypes.String,
  },
  {
    key: 'identification_class',
    type: JsonSchemaAllTypes.String,
  },
  {
    key: 'identification_number',
    type: JsonSchemaAllTypes.String,
  },
  {
    key: 'birthdate',
    type: JsonSchemaAllTypes.Date,
  },
  {
    key: 'phone_number',
    type: JsonSchemaAllTypes.String,
  },
  {
    key: 'email_address',
    type: JsonSchemaAllTypes.String,
  },
];

const TooltipContainer = styled.span`
  padding: ${Spacing[1]};
`;

const getFieldsJsonSchema = (
  object: string,
  jsonSchema: JsonSchema,
  schemaForFieldPath: ReturnType<typeof useSchemaForFieldPath>,
  objectKey?: WorkflowObjectKey,
): { jsonSchema: JsonSchemaObject; syntheticAccountsSchema: JsonSchemaObject } => {
  if (!object || !objectKey) {
    return {
      jsonSchema: { type: JsonSchemaTypes.Object, properties: {} },
      syntheticAccountsSchema: { type: JsonSchemaTypes.Object, properties: {} },
    };
  }
  const fieldsPath = `${object}.fields`;
  const fieldSchema = (schemaForFieldPath(fieldsPath, jsonSchema).schema ?? {
    type: JsonSchemaTypes.Object,
    label: 'fields',
    properties: {},
  }) as JsonSchemaObject;
  const accountAdditions: Record<string, JsonSchema> =
    objectKey !== WorkflowFieldObjectKey.Account
      ? {}
      : ACCOUNT_PRIMARY_PROPERTY_CONFIGS.reduceRight(
          (previousValue, currentValue) => {
            // account primary properties have been migrated to fields so we should hit this 100% of the time in practice.
            if (fieldSchema.properties?.[currentValue.key] != null) {
              return previousValue;
            }
            if (currentValue.type === JsonSchemaTypes.String) {
              previousValue[currentValue.key] = {
                type: currentValue.type,
              };
            } else if (currentValue.type === JsonSchemaCustomTypes.Date) {
              previousValue[currentValue.key] = {
                type: JsonSchemaTypes.String,
                format: 'date',
              };
            } else {
              throw new Error('unexpected account field type');
            }
            return previousValue;
          },
          {} as Record<string, JsonSchema>,
        );
  return {
    jsonSchema: {
      ...fieldSchema,
      properties: {
        ...fieldSchema.properties,
        ...accountAdditions,
      },
    },
    syntheticAccountsSchema: { type: JsonSchemaTypes.Object, properties: accountAdditions },
  };
};

interface ParamsUpdateObjectV1Props extends ParamsBaseProps {
  editable: boolean;
  errors: WorkflowError[];
  trigger?: Trigger;
  usableObjects: UsableObjectConfig[];
  params: WorkflowActionUpdateObjectV1['params'];
  updateActionParams: (newParams: WorkflowActionUpdateObjectV1['params']) => void;
}

const transformFields = (fields: StringKeyValue) => {
  const results: StringKeyValue = {};
  Object.entries(fields).forEach(([key, value]) => {
    if (typeof value === 'string') {
      results[key] = `{{${callHelper(value)}}}`;
    } else {
      results[key] = transformFields(value);
    }
  });
  return results;
};

const transformValues = (values: KeyValue) => {
  const results: StringKeyValue = {};
  Object.entries(values).forEach(([key, value]) => {
    if (typeof value === 'string') {
      results[key] = value;
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      results[key] = JSON.stringify(value);
    } else {
      results[key] = transformValues(value);
    }
  });
  return results;
};

const ParamsUpdateObjectV1: VFC<ParamsUpdateObjectV1Props> = ({
  step,
  editable,
  errors,
  params,
  trigger,
  usableObjects,
  updateActionParams,
  updateStep,
}: ParamsUpdateObjectV1Props) => {
  const schemaForFieldPath = useSchemaForFieldPath();
  const [showObjectFields, { toggle: toggleObjectFields, disable: closeObjectFields }] =
    useToggle(false);

  const possibleObjects = useMemo(() => {
    return filterUsableObjects({
      usableObjects,
      options: {
        permittedObjects: [
          WorkflowFieldObjectKey.Account,
          WorkflowFieldObjectKey.Case,
          WorkflowFieldObjectKey.Inquiry,
          WorkflowFieldObjectKey.Transaction,
        ],
        excludeNonPersonaObjects: true,
      },
    });
  }, [usableObjects]);

  // when we change the object it is likely that the new object does not have compatible fields as the previous
  // object so to avoid confusion we clear it out.
  const handleSelectObjectRevamped = (value: string) => {
    updateActionParams({
      ...params,
      operators: {},
      fields: {},
      values: {},
      object: value,
    });
  };

  // for source object
  const { jsonSchema, tokensToDisplayValue } = useMemo(() => {
    return generateJsonSchemaConfig({ usableObjects });
  }, [usableObjects]);

  const handleChangeOperatorsAndObjects = (operatorsAndValues: {
    keyOperatorObject: Record<string, string>;
    keyValueObject: Record<string, string>;
  }) => {
    const { keyOperatorObject, keyValueObject } = operatorsAndValues;
    const flattenedFields: Record<string, string> = {};
    const flattenedValues: Record<string, any> = {};

    // since the UI revamp keyValueObject could be a nested hash. we have to flatten and reconstruct
    flattenKeyValue(keyValueObject).forEach(([key, value]) => {
      const handleBarsMatch = getHandlebars(value as string);
      if (handleBarsMatch.length > 0) {
        // this feels counterintuitive but the backend supports resolved_input for fields
        // that means that we only support one single method chain here
        flattenedFields[key] = handleBarsMatch[0];
      } else {
        flattenedValues[key] = value;
      }
    });
    updateActionParams({
      ...params,
      operators: {
        ...keyOperatorObject,
      },
      fields: flattenedEntriesToHash(flattenedFields),
      values: flattenedEntriesToHash(flattenedValues),
    });
  };

  const { hasErrorFor, renderErrorMessageFor } =
    useWorkflowError<keyof WorkflowActionUpdateObjectV1['params']>(errors);

  const keyValueObject = useMemo(() => {
    return merge(
      transformFields(params.fields),
      params.values == null ? {} : transformValues(params.values),
    );
  }, [params.fields, params.values]);

  const unsetOperatorFields = useMemo(() => {
    const flattenedOperators = params.operators == null ? [] : flattenKeyValue(params.operators);
    // flattenedOperators is an array of size 2 arrays where the first element is the full field and the second element is the operator
    return new Set(
      flattenedOperators.filter((entry) => entry[1] === Operator.Unset).map((entry) => entry[0]),
    );
  }, [params.operators]);
  const displayKeyValueObject = useMemo(() => {
    const vt = valueTransformer(tokensToDisplayValue);
    return mapValues(
      (value) => formatHandlebarValue(value as string, vt.getDisplayValue),
      Object.fromEntries(flattenKeyValue(keyValueObject)),
    );
  }, [keyValueObject, tokensToDisplayValue]);

  const { jsonSchema: fieldsJsonSchema, syntheticAccountsSchema } = useMemo(
    () =>
      getFieldsJsonSchema(
        valueTransformer(tokensToDisplayValue).getDisplayValue(params['object']),
        jsonSchema,
        schemaForFieldPath,
        possibleObjects.find((obj) => obj.value === params['object'])?.workflowObjectKey,
      ),
    [tokensToDisplayValue, params, jsonSchema, possibleObjects, schemaForFieldPath],
  );

  const fields = Object.entries({
    ...Object.fromEntries(flattenKeyValue<string>(params.operators || {})),
    ...displayKeyValueObject,
  }).sort((left, right) => left[0].localeCompare(right[0]));
  return (
    <>
      <Spacer height={2} />
      <ParamLabel labelText='Object' htmlFor='params-update-object__object-input' />
      <ReactSelect
        value={params['object']}
        options={possibleObjects}
        onChange={handleSelectObjectRevamped}
        disabled={!editable}
        id='params-update-object__object'
        hasError={hasErrorFor('object')}
      />
      {renderErrorMessageFor('object')}
      <Spacer height={4} />
      {showObjectFields && (
        <MaximizablePanel
          outsideExceptionalIdPrefixes={['edit-object-key-value-pairs-panel__']}
          title={`Update object: ${startCase(
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            possibleObjects.find((obj) => obj.value === params['object'])?.workflowObjectKey,
          )} fields`}
          onOutsideClickAndEscPress={closeObjectFields}
        >
          <EditObjectKeyValuePairsPanel
            object={params.object}
            keyValueObject={keyValueObject}
            keyOperatorObject={params.operators ?? {}}
            updateOperatorsAndObjects={handleChangeOperatorsAndObjects}
            objectFieldsJsonSchema={fieldsJsonSchema}
            usableObjectsJsonSchema={jsonSchema}
            stepTokensToDisplayValue={tokensToDisplayValue}
            syntheticAccountsJsonSchema={syntheticAccountsSchema}
          />
        </MaximizablePanel>
      )}
      <Accordion defaultExpanded title='Object Fields'>
        {renderErrorMessageFor('fields')}
        <Spacer height={2} />
        <FieldsList
          fields={fields}
          fieldKey='fields'
          editable={editable}
          toggleEditPanel={toggleObjectFields}
          tokensToDisplayValue={tokensToDisplayValue}
          errors={errors}
          placeholderEmptyText={
            keyValueObject == null ? 'Please select an object' : 'No fields selected'
          }
          formatValue={(fieldKey, fieldValue) =>
            unsetOperatorFields.has(fieldKey) ? '(unset)' : fieldValue
          }
        />
      </Accordion>
      <Spacer height={2} />
      <StepAdvancedConfigurationWithErrorHandlingPanel
        step={step}
        trigger={trigger}
        updateStep={updateStep}
        disabled={!editable}
      >
        <>
          <Spacer height={2} /> <ParamLabel size='md' labelText='Empty fields' />
          <Checkbox
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              updateActionParams({
                ...params,
                'skip-empty-fields': e.target.checked === true,
              });
            }}
            checked={params['skip-empty-fields'] === true}
            label={
              <span>
                Do not update field if new value is empty
                <TooltipContainer>
                  <Tooltip
                    trigger='hover'
                    tooltip={
                      <Paragraph color='primary' size='sm'>
                        Checking the box ensures the step does not override populated fields with
                        empty or null values
                      </Paragraph>
                    }
                    hideArrow={true}
                  >
                    <CircleInfoIcon />
                  </Tooltip>
                </TooltipContainer>
              </span>
            }
            disabled={!editable}
          />
        </>
      </StepAdvancedConfigurationWithErrorHandlingPanel>
    </>
  );
};

export default ParamsUpdateObjectV1;
